import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Zap, Plus, Mail, MessageSquare, Send } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const AutomationBuilder = () => {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [triggerEvent, setTriggerEvent] = useState("");
  const [actionType, setActionType] = useState("");
  const [recipientType, setRecipientType] = useState("");
  const [messageTemplate, setMessageTemplate] = useState("");
  const queryClient = useQueryClient();

  const { data: workflows, isLoading } = useQuery({
    queryKey: ["automation-workflows"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("automation_workflows")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const createWorkflowMutation = useMutation({
    mutationFn: async (workflowData: any) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data, error } = await supabase
        .from("automation_workflows")
        .insert([{ 
          ...workflowData, 
          user_id: user.id,
          is_active: true,
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["automation-workflows"] });
      setIsCreateOpen(false);
      resetForm();
      toast.success("Automation workflow created successfully!");
    },
    onError: (error) => {
      console.error("Error creating workflow:", error);
      toast.error("Failed to create workflow");
    },
  });

  const toggleWorkflowMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { data, error } = await supabase
        .from("automation_workflows")
        .update({ is_active })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["automation-workflows"] });
      toast.success("Workflow updated successfully!");
    },
  });

  const resetForm = () => {
    setName("");
    setTriggerEvent("");
    setActionType("");
    setRecipientType("");
    setMessageTemplate("");
  };

  const handleCreateWorkflow = () => {
    if (!name || !triggerEvent || !actionType || !recipientType) {
      toast.error("Please fill in all required fields");
      return;
    }

    const actions = [{
      type: actionType,
      recipient: recipientType,
      template: messageTemplate,
    }];

    createWorkflowMutation.mutate({
      name,
      trigger_event: triggerEvent,
      actions,
      description: `Send ${actionType} to ${recipientType} when ${triggerEvent}`,
    });
  };

  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case "email": return <Mail className="h-4 w-4" />;
      case "sms": return <MessageSquare className="h-4 w-4" />;
      case "whatsapp": return <Send className="h-4 w-4" />;
      default: return <Zap className="h-4 w-4" />;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Automation Workflows
            </CardTitle>
            <CardDescription>
              Set up automated communications based on triggers
            </CardDescription>
          </div>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create Workflow
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create Automation Workflow</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Workflow Name</Label>
                  <Input 
                    placeholder="e.g., Invoice Reminder"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Trigger Event</Label>
                  <Select value={triggerEvent} onValueChange={setTriggerEvent}>
                    <SelectTrigger>
                      <SelectValue placeholder="When should this run?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="invoice_created">Invoice Created</SelectItem>
                      <SelectItem value="invoice_overdue">Invoice Overdue</SelectItem>
                      <SelectItem value="payment_received">Payment Received</SelectItem>
                      <SelectItem value="project_completed">Project Completed</SelectItem>
                      <SelectItem value="new_service_request">New Service Request</SelectItem>
                      <SelectItem value="task_assigned">Task Assigned</SelectItem>
                      <SelectItem value="meeting_scheduled">Meeting Scheduled</SelectItem>
                      <SelectItem value="subscription_expiring">Subscription Expiring</SelectItem>
                      <SelectItem value="new_client">New Client Added</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Action Type</Label>
                  <Select value={actionType} onValueChange={setActionType}>
                    <SelectTrigger>
                      <SelectValue placeholder="How to notify?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Send Email</SelectItem>
                      <SelectItem value="sms">Send SMS</SelectItem>
                      <SelectItem value="whatsapp">Send WhatsApp (Template)</SelectItem>
                      <SelectItem value="all">Send All (Email + SMS + WhatsApp)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Recipient</Label>
                  <Select value={recipientType} onValueChange={setRecipientType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Who receives this?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="client">Client</SelectItem>
                      <SelectItem value="team">Team Members</SelectItem>
                      <SelectItem value="assigned_user">Assigned User</SelectItem>
                      <SelectItem value="project_owner">Project Owner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {actionType === "whatsapp" && (
                  <div className="space-y-2">
                    <Label>WhatsApp Template Name</Label>
                    <Input 
                      placeholder="invoice_reminder"
                      value={messageTemplate}
                      onChange={(e) => setMessageTemplate(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Use an approved WhatsApp template name from your Infobip dashboard
                    </p>
                  </div>
                )}

                <Button onClick={handleCreateWorkflow} className="w-full">
                  Create Automation
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8">Loading workflows...</div>
        ) : !workflows || workflows.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No automation workflows yet. Create your first one!
          </div>
        ) : (
          <div className="space-y-4">
            {workflows.map((workflow) => (
              <Card key={workflow.id} className="border-l-4 border-l-primary">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold">{workflow.name}</h4>
                        <Switch
                          checked={workflow.is_active}
                          onCheckedChange={(checked) =>
                            toggleWorkflowMutation.mutate({
                              id: workflow.id,
                              is_active: checked,
                            })
                          }
                        />
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        {workflow.description}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline">
                          Trigger: {workflow.trigger_event.replace(/_/g, " ")}
                        </Badge>
                        {Array.isArray(workflow.actions) && workflow.actions.map((action: any, idx: number) => (
                          <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                            {getActionIcon(action.type)}
                            {action.type}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
